import {
  Directive,
  ElementRef,
  Renderer2,
  ComponentFactoryResolver,
  input,
  effect,
  ViewContainerRef,
  Input,
  Output,
  EventEmitter,
  type ComponentRef,
} from '@angular/core';
import { delay } from '@local/ts-infra';
import type { TypingViewMode } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ResourcePanelComponent } from 'src/app/bar/views/results/components/resource-panel/resource-panel.component';

@UntilDestroy()
@Directive({
  selector: '[customResourcePanel]',
})
export class CustomResourcePanelDirective {
  private selectedIndex: number;
  private componentsRef: ComponentRef<ResourcePanelComponent>[] = [];
  private isFinished: boolean = false;

  resources = input<[]>();
  typing = input<boolean>();
  viewMode = input<TypingViewMode>('interactive');
  @Input() isResourceLookup: boolean;
  @Output() completeRender = new EventEmitter();

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private componentFactoryResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef
  ) {
    effect(
      () => {
        if (this.isResourceLookup && this.viewMode()) {
          this.initComponents(this.typing(), this.resources());
        }
      },
      { allowSignalWrites: true }
    );
  }

  private async initComponents(typing: boolean, resources) {
    const elements = this.el.nativeElement.querySelectorAll('[data-resource]');
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ResourcePanelComponent);

    for (const element of elements) {
      const dataIndex = element.getAttribute('data-resource');
      if (!dataIndex) continue;
      const index = parseInt(dataIndex, 10);

      if (!resources[index]) continue;
      let componentRef = this.componentsRef[index];

      let isNew = false;
      if (!componentRef || componentRef.hostView.destroyed) {
        isNew = true;
        componentRef = this.viewContainerRef.createComponent(componentFactory);
        componentRef.instance.model.set(resources[index]);
        componentRef.instance.useAsResultItem = true;
        componentRef.instance.index = this.componentsRef.length;
        componentRef.instance.mouseover.pipe(untilDestroyed(this)).subscribe((index) => {
          this.selectedIndex = index;
          this.updateHover();
        });
        componentRef.instance.mouseout.pipe(untilDestroyed(this)).subscribe(() => {
          this.selectedIndex = null;
          this.updateHover();
        });
        componentRef.instance.useAsResultItem = true;
        this.componentsRef[index] = componentRef;
      }

      if (componentRef.location.nativeElement.parentNode) {
        componentRef.location.nativeElement.parentNode.removeChild(componentRef.location.nativeElement);
      }
      if (isNew && this.viewMode() === 'interactive') {
        await delay(80);
      }
      this.renderer.appendChild(element, componentRef.location.nativeElement);
      element.setAttribute('data-resource', null);
    }

    if (elements.length === resources?.length && !this.isFinished) {
      this.isFinished = true;
      this.completeRender.emit();
    }
  }

  private updateHover() {
    this.componentsRef.forEach((componentRef, index) => {
      componentRef.instance.hovered = !!(index === this.selectedIndex);
    });
  }
}
